import Papa from 'papaparse';

const SHEET_ID = '1V4F4RLRpAtlw3np7jUHniEdX0DwH3rxtUDM7Frp2IPQ';
const GID = '0'; // Use 0 for the first sheet
const CACHE_KEY = 'aol_programs_data';
const CACHE_TIMESTAMP_KEY = 'aol_programs_timestamp';
const CACHE_VERSION_KEY = 'aol_programs_cache_version';
const CACHE_CHECKSUM_KEY = 'aol_programs_checksum';
const CACHE_VERSION = '1.0'; // Bump this to invalidate all caches
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second

// Simple checksum function for data integrity
const generateChecksum = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
};

// Check if cached data is still valid
const getCachedData = (allowStale = false) => {
  try {
    const cachedData = localStorage.getItem(CACHE_KEY);
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    const version = localStorage.getItem(CACHE_VERSION_KEY);

    // Check version first
    if (version !== CACHE_VERSION) {
      console.log('Cache version mismatch, invalidating cache');
      clearCache();
      return null;
    }

    if (cachedData && timestamp) {
      const age = Date.now() - parseInt(timestamp, 10);

      // If allowing stale data, return it regardless of age
      if (allowStale) {
        const data = JSON.parse(cachedData);
        data._isStale = age >= CACHE_DURATION;
        data._cacheAge = age;
        return data;
      }

      // Normal cache check
      if (age < CACHE_DURATION) {
        return JSON.parse(cachedData);
      }
    }
  } catch (error) {
    console.warn('Cache read failed:', error);
  }
  return null;
};

// Clear cache
const clearCache = () => {
  try {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    localStorage.removeItem(CACHE_VERSION_KEY);
    localStorage.removeItem(CACHE_CHECKSUM_KEY);
  } catch (error) {
    console.warn('Cache clear failed:', error);
  }
};

// Save data to cache
const setCachedData = (data, csvChecksum) => {
  try {
    // Remove internal metadata before caching
    const cleanData = { ...data };
    delete cleanData._isStale;
    delete cleanData._cacheAge;

    localStorage.setItem(CACHE_KEY, JSON.stringify(cleanData));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
    localStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION);
    if (csvChecksum) {
      localStorage.setItem(CACHE_CHECKSUM_KEY, csvChecksum);
    }
  } catch (error) {
    console.warn('Cache write failed:', error);
  }
};

// Sleep utility for retry delays
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Fetch with retry logic
const fetchWithRetry = async (url, retries = MAX_RETRIES) => {
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const response = await fetch(url, { mode: 'cors' });
      if (response.ok) {
        return response;
      }
      throw new Error(`HTTP ${response.status}`);
    } catch (error) {
      if (attempt === retries - 1) throw error;

      // Exponential backoff: 1s, 2s, 4s
      const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt);
      console.log(`Retry ${attempt + 1}/${retries} after ${delay}ms...`);
      await sleep(delay);
    }
  }
};

export const fetchProgramData = async (forceRefresh = false) => {
  // Check for fresh cache first
  if (!forceRefresh) {
    const cachedData = getCachedData();
    if (cachedData) {
      return cachedData;
    }
  }

  // Stale-while-revalidate: return stale data immediately if available
  const staleData = getCachedData(true);
  if (staleData && !forceRefresh) {
    // Return stale data immediately
    // Fetch fresh data in background (fire and forget)
    fetchFreshData().catch(console.error);
    return staleData;
  }

  // No cache or forced refresh: fetch fresh data synchronously
  return await fetchFreshData();
};

// Separate function for fetching fresh data
const fetchFreshData = async () => {
  try {
    // Try different URL formats for Google Sheets CSV export
    const urls = [
      `https://docs.google.com/spreadsheets/d/${SHEET_ID}/export?format=csv&gid=${GID}`,
      `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:csv&gid=${GID}`,
      `https://docs.google.com/spreadsheets/d/${SHEET_ID}/export?format=csv`
    ];

    let response;
    let lastError;

    for (const url of urls) {
      try {
        response = await fetchWithRetry(url);
        if (response) break;
      } catch (error) {
        lastError = error;
      }
    }

    if (!response || !response.ok) {
      throw lastError || new Error('All URL attempts failed');
    }

    const csvData = await response.text();
    const csvChecksum = generateChecksum(csvData);

    // Check if data actually changed
    const cachedChecksum = localStorage.getItem(CACHE_CHECKSUM_KEY);
    if (cachedChecksum === csvChecksum) {
      console.log('Data unchanged, using cached version');
      const cached = getCachedData(true);
      if (cached) {
        // Update timestamp but keep same data
        localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
        return cached;
      }
    }
    
    // Parse CSV data using Papa Parse
    const parsedData = Papa.parse(csvData, {
      header: false,
      skipEmptyLines: true,
      transform: (value) => value.trim()
    });

    // Data validation
    if (parsedData.errors.length > 0) {
      console.warn('CSV parsing errors:', parsedData.errors);
      // Check for critical errors
      const criticalErrors = parsedData.errors.filter(e => e.type === 'Quotes' || e.type === 'FieldMismatch');
      if (criticalErrors.length > 0) {
        throw new Error('Critical CSV parsing errors detected');
      }
    }

    // Validate data structure
    if (!parsedData.data || parsedData.data.length < 2) {
      throw new Error('Invalid CSV: No data rows found');
    }

    // Transform the parsed data into program structure
    const programs = parsedData.data
      .slice(1) // Skip header row
      .filter(row => row && row.length >= 4 && row[0] && row[1]) // Filter out empty rows
      .map((row, index) => ({
        id: `program-${index}`,
        name: row[1] || '', // Event column
        description: getDescriptionByEventType(row[1] || ''),
        dates: [(row[0] || '').toString()], // Date range column
        times: [''],
        venues: [(row[2] || '').toString()], // Location column
        isOnline: [((row[2] || '').toLowerCase().includes('online') || (row[2] || '').toLowerCase().includes('zoom'))],
        registrationLinks: [(row[3] || '#').toString()], // Link column
        ageGroup: getAgeGroupFromEvent(row[1] || '')
      }));

    // Validate we got valid programs
    if (programs.length === 0) {
      throw new Error('No valid programs found in CSV');
    }

    // Group programs by age group
    const groupedPrograms = groupProgramsByAgeGroup(programs);

    const programData = {
      siteInfo: {
        title: "Art of Living Intuition Program with Vaishali Rathi ji and team",
        subtitle: "Hyderabad",
        logo: "/aol-logo.jpeg"
      },
      ipLevel2: {
        id: "ip-level-2",
        title: "Intuition Process Level 2",
        eligibility: "Intuition Process Level 1 - Kids, Juniors, Teens",
        description: "Advanced intuition development program for graduates of IP Level 1",
        color: "bg-purple-100",
        programs: programs.filter(p => p.name.toLowerCase().includes('ip level 2') || p.name.toLowerCase().includes('level 2'))
      },
      ageGroups: groupedPrograms
    };

    // Final validation
    if (!programData.ageGroups || programData.ageGroups.length === 0) {
      throw new Error('Program data validation failed: no age groups');
    }

    // Cache the data with checksum
    setCachedData(programData, csvChecksum);

    return programData;

  } catch (error) {
    console.error('Error fetching program data:', error);

    // Try to return stale cache as last resort
    const staleData = getCachedData(true);
    if (staleData) {
      console.log('Using stale cache as fallback');
      return staleData;
    }
    
    // Fallback to static data if Google Sheets fails
    return {
      siteInfo: {
        title: "Art of Living Intuition Program with Vaishali Rathi ji and team",
        subtitle: "Hyderabad",
        logo: "/aol-logo.jpeg"
      },
      ipLevel2: {
        id: "ip-level-2",
        title: "Intuition Process Level 2",
        eligibility: "Intuition Process Level 1 - Kids, Juniors, Teens",
        description: "Advanced intuition development program for graduates of IP Level 1",
        color: "bg-purple-100",
        programs: [
          {
            id: "ip-level-2-coming-soon",
            name: "IP Level 2",
            description: "Advanced techniques for enhanced intuitive abilities",
            dates: ["Coming Soon"],
            times: ["TBD"],
            venues: ["TBD"],
            isOnline: [false],
            registrationLinks: ["#"]
          }
        ]
      },
      ageGroups: [
        {
          id: "Juniors",
          age: "5-8 years",
          title: "Programs for Juniors",
          description: "Foundational programs for young children to build intuition and basic skills",
          color: "bg-yellow-100",
          programs: [
            {
              id: "sample-juniors",
              name: "IP Juniors",
              description: "Build focus, creativity and intuition through fun exercises",
              dates: ["Please check Google Sheets for latest dates"],
              times: ["TBD"],
              venues: ["TBD"],
              isOnline: [false],
              registrationLinks: ["#"]
            }
          ]
        },
        {
          id: "Kids",
          age: "8-13 years",
          title: "Programs for Kids",
          description: "Programs designed for growing children to enhance focus and build confidence",
          color: "bg-green-100",
          programs: [
            {
              id: "sample-kids",
              name: "IP Kids",
              description: "Intuition training with concentration exercises",
              dates: ["Please check Google Sheets for latest dates"],
              times: ["TBD"],
              venues: ["TBD"],
              isOnline: [false],
              registrationLinks: ["#"]
            }
          ]
        },
        {
          id: "Teens",
          age: "13-18 years",
          title: "Programs for Teens",
          description: "For teenagers to develop leadership skills and emotional intelligence",
          color: "bg-blue-100",
          programs: [
            {
              id: "sample-teens",
              name: "IP Teens",
              description: "Intuition and leadership development",
              dates: ["Please check Google Sheets for latest dates"],
              times: ["TBD"],
              venues: ["TBD"],
              isOnline: [false],
              registrationLinks: ["#"]
            }
          ]
        }
      ]
    };
  }
};

function getDescriptionByEventType(eventName) {
  const name = eventName.toLowerCase();

  if (name.includes('ip kids') || name.includes('intuition process kids')) {
    return 'Intuition training with concentration exercises for kids';
  }
  if (name.includes('ip juniors') || name.includes('intuition process juniors')) {
    return 'Build focus, creativity and intuition through fun exercises for juniors';
  }
  if (name.includes('ip teens') || name.includes('intuition process teens')) {
    return 'Intuition and leadership development for teens';
  }
  if (name.includes('ip level 2') || name.includes('level 2')) {
    return 'Advanced techniques for enhanced intuitive abilities';
  }
  if (name.includes('medha yoga')) {
    return 'Enhance memory, concentration and learning abilities through yogic techniques';
  }
  if (name.includes('utkarsh yoga')) {
    return 'Holistic development program for physical, mental and emotional growth';
  }

  return 'Intuition development program';
}

function getAgeGroupFromEvent(eventName) {
  const name = eventName.toLowerCase();

  if (name.includes('kids')) return 'Kids';
  if (name.includes('juniors')) return 'Juniors';
  if (name.includes('teens')) return 'Teens';
  if (name.includes('medha yoga')) return 'MedhaYoga';
  if (name.includes('utkarsh yoga')) return 'UtkarshYoga';

  return 'General';
}

function groupProgramsByAgeGroup(programs) {
  const ageGroups = [
    {
      id: "Juniors",
      age: "5-8 years",
      title: "Juniors (5-8 years)",
      description: "Foundational programs for young children to build intuition and basic skills",
      color: "bg-yellow-100",
      programs: []
    },
    {
      id: "Kids",
      age: "8-13 years",
      title: "Kids (8-13 years)",
      description: "Programs designed for growing children to enhance focus and build confidence",
      color: "bg-green-100",
      programs: []
    },
    {
      id: "Teens",
      age: "13-18 years",
      title: "Teens (13-18 years)",
      description: "For teenagers to develop leadership skills and emotional intelligence",
      color: "bg-blue-100",
      programs: []
    },
    {
      id: "MedhaYoga",
      age: "13-18 years",
      title: "Medha Yoga (13-18 years)",
      description: "Programs to enhance memory, concentration and learning abilities",
      color: "bg-orange-100",
      programs: []
    },
    {
      id: "UtkarshYoga",
      age: "8-13 years",
      title: "Utkarsh Yoga (8-13 years)",
      description: "Holistic development programs for overall growth and well-being",
      color: "bg-pink-100",
      programs: []
    }
  ];

  // Distribute programs to age groups
  programs.forEach(program => {
    const ageGroup = ageGroups.find(group => group.id === program.ageGroup);
    if (ageGroup) {
      ageGroup.programs.push(program);
    }
  });

  return ageGroups;
}

// Export utility functions for components to use
export { clearCache, getCachedData };